import React, { useState, useEffect, useRef } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useTheme, Container, TextField, Button, Stack, Box, Checkbox, Autocomplete, FormControlLabel, Typography, Modal, Grid, CircularProgress, Stepper, Step, StepLabel, StepContent, Tooltip } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Controller, SubmitHandler, useForm, FormProvider } from 'react-hook-form';
import { useAmaV2Context } from 'context/ama-v2-provider';
import { EditMode, FileStatus, SnapshotV2Status } from '../../../../data/enums';
import { DevTool } from '@hookform/devtools';
import { integer } from 'aws-sdk/clients/cloudfront';
import { AmaV2Client, AmaV2Snapshot } from 'context/ama-v2-provider/ama-v2.types';
import { useAmaS3Manager } from 'features/ama_v2/hooks/useAmaS3Manager';
import FileUploaderItem from 'features/ama_v2/components/file-uploader-item';

const POST_UPLOAD_DELAY_MS = 7000;


interface IFormInputs {
  client_id: string;
  academic_year: string;
  snapshot_date: string;
  description: string;
  notes: string;
  is_active: boolean;
  client_comparison: boolean;
}

interface IClientXtd extends AmaV2Client {
  label: string;
}

interface ISnapshotAddUpdateProps {
  mode: EditMode;
  setEditMode: React.Dispatch<React.SetStateAction<EditMode>>;
};

export default function SnapshotAddUpdate(props: ISnapshotAddUpdateProps) {

  const theme = useTheme();
  const { loadClients, clientsState, snapshotsState, loadSnapshots, setSelectedSnapshot, addSnapshot, updateSnapshot, deleteSnapshotFile, downloadSnapshot, clearSnapshotsError, selectedAcademicYear, get_active_snapshot, isSnapshotActive } = useAmaV2Context();
  const formProps = useForm<IFormInputs>({ defaultValues: getDefaultFormValues(snapshotsState.selectedSnapshot, selectedAcademicYear) });  
  const [selectedClient, setSelectedClient] = useState<IClientXtd | null>(null);
  const [snapshotDate, setSnapshotDate] = useState<Dayjs | null>(null);
  const [cleared, setCleared] = useState<boolean>(false);  
  const [isUploading, setIsUploading] = useState<boolean>(false);  
  const [saveStep, setSaveStep] = useState<integer>(0);
  const [showDeleteFileModal, setShowDeleteFileModal] = useState<boolean>(false);
  const [showProcessingModal, setShowProcessingModal] = useState<boolean>(false);
  const [showOlderSnapshotModal, setShowOlderSnapshotModal] = useState<boolean>(false);
  const olderSnapshotConfirmResolver = useRef<((proceed: boolean) => void) | null>(null);

  const { fileUploads, setFileForUpload, uploadAll, setFileStatus } = useAmaS3Manager({
    bucketName: process.env.REACT_APP_AWS_V2_S3_BUCKET!,
    clientId: formProps.getValues('client_id'),
    academicYear: formProps.getValues('academic_year'),
    snapshotDate: formProps.getValues('snapshot_date'),
    existingFileKey: snapshotsState.selectedSnapshot?.bronze_path || snapshotsState.selectedSnapshot?.ingest_path || null,
    existingStatus: snapshotsState.selectedSnapshot?.bronze_path ? FileStatus.Uploaded : undefined
  });

  useEffect(() => {
    if (cleared) {
      const timeout = setTimeout(() => {
        setCleared(false);
      }, 1500);

      return () => clearTimeout(timeout);
    }
  }, [cleared]);

  useEffect(() => {
    console.log(`SnapshotAddUpdate mount - ${snapshotsState.selectedSnapshot?.client_id}`);
  }, []);

  // Effect: map backend snapshot lifecycle to tile FileStatus.
  // Specification:
  //   Processing statuses -> Processing: INGESTING_BRONZE, BRONZE_COMPLETE, PROCESSING_SILVER, SILVER_COMPLETE, PROCESSING_GOLD, GOLD_COMPLETE
  //   Uploaded statuses -> Uploaded: COMPLETED, COMPLETED_WITH_WARNINGS
  //   Error status -> Error: FAILED_WITH_ERRORS
  //   WAITING -> retain current (Empty, Collected, Uploading) without forced change
  useEffect(() => {
    if (!fileUploads || fileUploads.length !== 1) return;
    const snap = snapshotsState.selectedSnapshot;
    if (!snap) return;
    const currentRaw = fileUploads[0].FileStatus as string | undefined;
    const currentStatus: FileStatus = Object.values(FileStatus).includes(currentRaw as FileStatus)
      ? currentRaw as FileStatus
      : FileStatus.Empty;
    const mapped = mapSnapshotV2StatusToTileStatus(snap.status as SnapshotV2Status | undefined, currentStatus);
    if (mapped !== currentStatus) setFileStatus(mapped);
  }, [snapshotsState.selectedSnapshot?.status, fileUploads, setFileStatus]);


  const onSubmit: SubmitHandler<IFormInputs> = async (data: IFormInputs, e) => {

    // Saving interstitial - step 0
    setSaveStep(0);

    // Resolve selected snapshot key values from form/snapshot context once.
    const selectedClientId = (props.mode === EditMode.Add)
      ? data.client_id.trim()
      : (snapshotsState.selectedSnapshot?.client_id || data.client_id.trim());
    const selectedAcademicYear = (props.mode === EditMode.Add)
      ? data.academic_year.trim()
      : (snapshotsState.selectedSnapshot?.academic_year || data.academic_year.trim());
    const selectedSnapshotDate = (props.mode === EditMode.Add)
      ? data.snapshot_date.trim()
      : (snapshotsState.selectedSnapshot?.snapshot_date || data.snapshot_date.trim());

    // Always deny submit when another snapshot for this client is already processing.
    // This must happen before any save/upload work starts.
    const activeSnapshot = selectedClientId ? await get_active_snapshot(selectedClientId) : null;
    if (activeSnapshot && activeSnapshot.is_processing === true) {
      setShowProcessingModal(true);
      return;
    }

    // File upload is trigger to start ETL processing.
    // Run all active-snapshot checks before snapshot mutation so cancel does not leave UI in loading state.
    const pendingUpload = hasPendingUploads();
    if (pendingUpload) {
      if (activeSnapshot && isOlderThanActiveSnapshot(selectedAcademicYear, selectedSnapshotDate, activeSnapshot)) {
        const shouldProceed = await askOlderSnapshotUploadConfirmation(
          setShowOlderSnapshotModal,
          olderSnapshotConfirmResolver
        );
        if (!shouldProceed) {
          return; // Cancel operation before save/upload starts
        }
      }
    }

    // Common payload (allow clearing optional fields)
    const trimOrEmpty = (v: string) => (v === undefined || v === null) ? '' : v.trim();
    const payload: Partial<AmaV2Snapshot> = {
      description: trimOrEmpty(data.description),
      notes: trimOrEmpty(data.notes),
      is_active: !!data.is_active,
      client_comparison: !!data.client_comparison,
    };

    // Add or Update based on EditMode
    let success = false;
    if (props.mode === EditMode.Add) {
      success = await addSnapshot(
        selectedClientId,
        selectedAcademicYear,
        selectedSnapshotDate,
        payload);
    } else if (props.mode === EditMode.Edit && snapshotsState.selectedSnapshot) {
      success = await updateSnapshot(
        snapshotsState.selectedSnapshot.client_id,
        snapshotsState.selectedSnapshot.academic_year,
        snapshotsState.selectedSnapshot.snapshot_date,
        payload);
    }

    // On error, abort further steps - TODO - show error message?
    if (!success) return;

    // Upload after snapshot save has succeeded
    if (pendingUpload) {
      // Proceed with upload since no active processing is underway
      setSaveStep(1);
      setIsUploading(true);
      await uploadAll();
      await delay(POST_UPLOAD_DELAY_MS);
    }


    // First reload of snapshots (may still show WAITING or early status)
    await loadClients();
    await loadSnapshots();    
    setSelectedSnapshot(null, null, null);
    props.setEditMode(EditMode.View);    
  };

  const handleDeleteFileButtonClick = () => {
    setShowDeleteFileModal(true);
  };

  const handleDeleteFileModalOkButtonClick = async () => {
    if (!snapshotsState.selectedSnapshot) return;
    setShowDeleteFileModal(false); // close modal immediately
    // If file only staged locally
    if (fileUploads && fileUploads.length === 1 && fileUploads[0].FileStatus === FileStatus.Collected) {
      setFileForUpload(null);
      return;
    }
    const success = await deleteSnapshotFile(
      snapshotsState.selectedSnapshot.client_id,
      snapshotsState.selectedSnapshot.academic_year,
      snapshotsState.selectedSnapshot.snapshot_date
    );
    if (success) setFileForUpload(null);
  };

  const handleDeleteFileModalCancelButtonClick = () => { setShowDeleteFileModal(false); };

  const selectedSnapshotCanDownload = Boolean(
    snapshotsState.selectedSnapshot &&
    isSnapshotActive(snapshotsState.selectedSnapshot).isActive &&
    clientsState.data.find(c => c.client_id === snapshotsState.selectedSnapshot?.client_id)?.active_snapshot?.file_path
  );

  const handleSnapshotDownloadButtonClick = async () => {
    if (!snapshotsState.selectedSnapshot || !selectedSnapshotCanDownload) return;
    const snapshot = snapshotsState.selectedSnapshot;
    const downloadUrl = await downloadSnapshot(
      snapshot.client_id,
      snapshot.academic_year,
      snapshot.snapshot_date,
    );

    if (!downloadUrl) return;
    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
  };

  const handleOlderSnapshotModalYesButtonClick = () => {
    setShowOlderSnapshotModal(false);
    olderSnapshotConfirmResolver.current?.(true);
    olderSnapshotConfirmResolver.current = null;
  };

  const handleOlderSnapshotModalCancelButtonClick = () => {
    setShowOlderSnapshotModal(false);
    olderSnapshotConfirmResolver.current?.(false);
    olderSnapshotConfirmResolver.current = null;
  };

  const handleCancelButtonClick = () => {
    setSelectedSnapshot(null, null, null);
    props.setEditMode(EditMode.View);    
  };

  const handleModalOkButtonClick = () => {
    clearSnapshotsError();
  };

  const LogUploadDetails = () => {
    if (!hasPendingUploads()) return;
    const pendingUploads =  fileUploads?.filter(item => item.FileStatus === FileStatus.Collected);
    if (pendingUploads && pendingUploads.length > 0){
      console.log('Count: ' + pendingUploads.length);
      pendingUploads.forEach((item)=> { console.log('Category: ' + item.Category + '; Name: ' + item.FileForUpload?.name + '; Size: ' + item.FileForUpload?.size + ' bytes' )});
    }
  };

  const logFormErrors = () => {
    console.log(`SnapshotAddUpdate.DEBUG :: client: ${selectedClient?.client_id}, form errors: ${formProps.formState.errors}`);
  }

  const handleClientSelection = (event: any, client: IClientXtd | null) => {
    setSelectedClient(client);
    formProps.setValue('client_id', client?.client_id || '');
    if (client) formProps.clearErrors('client_id');
  };

  const handleSnapshotDateSelection = (newDate: dayjs.Dayjs | null) => {
    setSnapshotDate(newDate);
    formProps.setValue('snapshot_date', newDate ? newDate.format('YYYYMMDD') : '');
    if (newDate) formProps.clearErrors('snapshot_date');
  };

  const getSaveButtonLabel_v2 = () : string => {
    if (hasPendingUploads()) {
      if (props.mode === EditMode.Add) return 'SAVE AND UPLOAD';
      return 'UPLOAD';
    } 

    return 'SAVE';
  };

  function hasPendingUploads() : boolean {
    if (!fileUploads || fileUploads.length === 0) return false;
    return fileUploads[0].FileStatus === FileStatus.Collected;
  }

  function hasUploadingUploads() : boolean {
    if (!fileUploads || fileUploads.length === 0) return false;
    return fileUploads[0].FileStatus === FileStatus.Uploading;
  }

  const delay = (timeMs: number) => {
    return new Promise(resolve => setTimeout(resolve, timeMs));
  }

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
      <Box id="inner" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minWidth: '600px' }}>
        <FormProvider {...formProps}>
          <form 
            noValidate 
            onSubmit={formProps.handleSubmit(onSubmit)}>

            {/* Loading overlay */}
            {(snapshotsState.status === 'loading' || isUploading) && (
              <Box 
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  background: 'rgba(255, 255, 255, 0.6)',
                  zIndex: 999,              
                }}>

                <Box 
                  sx={{ 
                    bgcolor: 'background.paper',
                    border: `1px solid ${theme.palette.grey[300]}`,
                    boxShadow: 24,
                    p: 4
                  }}>
                
                  <Stepper activeStep={saveStep} orientation="vertical" sx={{ width:'350px', backgroundColor:'white'}}>
                    {saveSteps.map((step, index) => (
                      <Step key={step.label}>
                        <StepLabel>
                          <Typography variant='h6' sx={{ mx: 2 }} color="text.secondary">
                            {step.label}
                          </Typography>
                        </StepLabel>
                        <StepContent>
                        <Typography variant='body1'  sx={{ mx: 2 }} color="text.secondary">
                            {step.description}
                          </Typography>
                        </StepContent>
                      </Step>
                    ))}
                  </Stepper>
                </Box>
              </Box>
            )}

            {/* Fixed width content wrapper that centers */}
            <Container sx={{ width: '600px', margin: 0, padding: 0, mb: 10 }}>

              {/* Section header */}
              {/* <Typography component="h6" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                Snapshot Details
              </Typography> */}

              <Stack spacing={2}>

                <Typography component="h6" variant="h6" sx={{ color: theme.palette.text.secondary, fontWeight: 'bold' }}>
                  Snapshot Details
                </Typography>

                <Grid container alignItems="center" sx={{ mb: 1 }}>
                  <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <FormControlLabel
                      label="Use for Client Comparison"
                      control={
                        <Controller
                          name="client_comparison"
                          control={formProps.control}
                          render={({ field }) => (
                            <Checkbox
                              color="primary"
                              checked={field.value}
                              onChange={(e, checked) => field.onChange(checked)}
                            />
                          )}
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <FormControlLabel
                      label="Active"
                      control={
                        <Controller
                          name="is_active"
                          control={formProps.control}
                          render={({ field }) => (
                            <Checkbox
                              color="primary"
                              checked={field.value}
                              onChange={(e, checked) => field.onChange(checked)}
                            />
                          )}
                        />
                      }
                    />
                  </Grid>
                </Grid>

                {/* Mode: Edit */}
                {(props.mode === EditMode.Edit) &&
                  <>
                    <TextField 
                      label='Client ID'
                      type='text'
                      disabled={true}
                      {...formProps.register('client_id')}
                    />
                    <Stack direction='row' spacing={2} sx={{ width: '100%' }}>
                      <TextField 
                        label='Academic Year'
                        type='text'
                        disabled={true}
                        sx={{ flex: 1 }}
                        inputProps={{ maxLength: 4 }}
                        {...formProps.register('academic_year')}
                      />
                      <TextField 
                        label='Snapshot Date'
                        type='text'
                        disabled={true}
                        sx={{ flex: 1 }}
                        {...formProps.register('snapshot_date')}
                      />  
                    </Stack>
                    <TextField 
                      label='Description (optional)'
                      type='text'
                      multiline
                      minRows={1}
                      {...formProps.register('description')}
                    />
                    <TextField 
                      label='Notes (optional)'
                      type='text'
                      multiline
                      minRows={2}
                      {...formProps.register('notes')}
                    />
                  </>
                }

                {/* Mode: Add */}     
                {(props.mode === EditMode.Add) &&
                  <>
                    <Controller
                      name="client_id"
                      control={formProps.control}
                      rules={{ required: 'Client selection is required.' }}
                      render={({ field }) => (
                        <Autocomplete
                          {...field}
                          disablePortal
                          id="client_id"
                          options={mapClientsXtd(clientsState.data)}
                          sx={{ width: '100%'}}
                          value={selectedClient}
                          onChange={handleClientSelection}
                          isOptionEqualToValue={(option, value) => option.client_id === value.client_id}
                          renderInput={(params) => 
                            <TextField
                              label="Client ID"
                              type='text'
                              error={Boolean(formProps.formState.errors.client_id)}
                              helperText={formProps.formState.errors.client_id?.message}
                              {...params} 
                            /> 
                          }
                        />
                      )}
                    />
                    <Stack direction='row' spacing={2} sx={{ width: '100%' }}>
                      <Controller
                        name="academic_year"
                        control={formProps.control}
                        rules={{ 
                          required: 'Academic year is required.',
                          pattern: { value: /^\d{4}$/, message: 'Academic year must be 4 digits (YYYY).' }
                        }}
                        render={({ field }) => (
                          <TextField 
                            {...field}
                            label='Academic Year (YYYY)'
                            type='text'
                            error={Boolean(formProps.formState.errors.academic_year)}
                            helperText={formProps.formState.errors.academic_year?.message}
                            sx={{ flex: 1 }}
                            inputProps={{ maxLength: 4 }}
                          />
                        )}
                      />
                      <Controller
                        name="snapshot_date"
                        control={formProps.control}
                        rules={{ required: 'Snapshot date is required.' }}
                        render={({ field }) => (
                          <DatePicker
                            {...field}
                            label={'Snapshot Date'}
                            value={snapshotDate}
                            onChange={handleSnapshotDateSelection}
                            views={['year', 'month', 'day']}
                            format='YYYY/MM/DD'
                            slotProps={{
                              field: { 
                                clearable: true, 
                                onClear: () => setCleared(true) 
                              },
                              textField: {
                                type: 'text',
                                error: Boolean(formProps.formState.errors.snapshot_date),
                                helperText: formProps.formState.errors.snapshot_date?.message,
                                sx: { flex: 1 }
                              }
                            }}
                          />
                        )}
                      />
                    </Stack>
                    <TextField 
                      label='Description (optional)'
                      type='text'
                      multiline
                      minRows={1}
                      {...formProps.register('description')}
                    />
                    <TextField 
                      label='Notes (optional)'
                      type='text'
                      multiline
                      minRows={2}
                      {...formProps.register('notes')}
                    />
                  </>
                }

                {/* Legacy v1 Manifest file uploader (kept for reference) */}
                {/* Manifest file uploader */}
                {/* { fileUploads && fileUploads.length > 0 &&
                  <>
                    <Typography component="h6" sx={{ color: theme.palette.text.secondary, mb: 2, pt: 2 }}>
                      Snapshot Files
                    </Typography>
                    <ManifestFileUploader 
                      Items={fileUploads} 
                      SetFileForUpload={setFileForUpload}
                      DeleteFile={handleDeleteFileButtonClick}/>
                  </>
                } */}

                {/* Manifest file uploader */}
                {/* <ManifestFileUploader 
                  Items={fileUploads} 
                  SetFileForUpload={setFileForUpload}
                  DeleteFile={handleDeleteFileButtonClick}/> */}

                {/* Single snapshot file uploader (v2) */}
                { fileUploads && fileUploads.length === 1 && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, pt: 2 }}>
                      <Typography component="h6" sx={{ color: theme.palette.text.secondary }}>
                        Snapshot File
                      </Typography>

                      {selectedSnapshotCanDownload && (
                        <Tooltip title="Download active snapshot file.">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<DownloadIcon fontSize="small" />}
                              aria-label="Download snapshot file"
                              onClick={handleSnapshotDownloadButtonClick}
                              sx={{
                                minWidth: 0,
                                px: 1.5,
                                py: 0.5,
                                textTransform: 'none',
                              }}
                            >
                              Download Active Snapshot
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                    <FileUploaderItem 
                      File={fileUploads[0]} 
                      SetFileForUpload={setFileForUpload}
                      DeleteFile={handleDeleteFileButtonClick}
                      Height={160}
                      CanDelete={(() => {
                        const status = snapshotsState.selectedSnapshot?.status as SnapshotV2Status | undefined;
                        return status === SnapshotV2Status.COMPLETED 
                          || status === SnapshotV2Status.COMPLETED_WITH_WARNINGS 
                          || status === SnapshotV2Status.FAILED_WITH_ERRORS;
                      })()} />
                  </>
                )}


                {/* Save/Cancel buttons */}
                <Stack spacing={2} direction="row" alignItems="center" justifyContent="flex-end" sx={{ pt: 4}}>
                  <Button type='submit' variant='contained' color='primary'>{getSaveButtonLabel_v2()}</Button>
                  <Button variant='outlined' color='primary' onClick={handleCancelButtonClick}>Cancel</Button>
                </Stack>   

              </Stack>

              {/* Test File Item templates */}
              {/* <br/>
              <FileUploaderItemStyleTester />  */}

              {/* Remove when dev complete */}
              <DevTool control={formProps.control}/>
                    
            </Container>
          </form>
        </FormProvider>

        {/* Error modal */}
        <Modal
          open={Boolean(snapshotsState.error)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description">
          <Box sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.grey[300]}`,
            boxShadow: 24,
            p: 4,}}>
            <Typography id="modal-modal-title" variant="h6" component="h2">
              Error
            </Typography>
            <Typography id="modal-modal-description" sx={{ mt: 2, mb: 4 }}>
              {snapshotsState.error}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button variant="contained" color="primary" onClick={handleModalOkButtonClick}>Ok</Button>
            </Box>
          </Box>
        </Modal>

        {/* Active snapshot processing modal */}
        <Modal
          open={showProcessingModal}
          aria-labelledby="modal-processing"
          aria-describedby="modal-processing">
          <Box sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 420,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.grey[300]}`,
            boxShadow: 24,
            p: 4,}}>
            <Typography id="modal-processing-title" variant="h6" component="h2">
              Warning
            </Typography>
            <Typography id="modal-processing-description" sx={{ mt: 2, mb: 4 }}>
              A snapshot is already currently being processed for this client, please try later.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => { setShowProcessingModal(false); props.setEditMode(EditMode.View); }}>
                Ok
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Delete snapshot file confirmation modal */}
        <Modal
          open={showDeleteFileModal}
          aria-labelledby="modal-file-delete"
          aria-describedby="modal-file-delete">
          <Box sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.grey[300]}`,
            boxShadow: 24,
            p: 4,}}>
            <Typography id="modal-delete-file-title" variant="h6" component="h2">
              Delete
            </Typography>
            <Typography id="modal-delete-file-description" sx={{ mt: 2, mb: 4 }}>
              Are you sure you want to delete the snapshot file in Bronze? If any Silver job artifacts exist, they will also be deleted.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button variant="contained" color="primary" sx={{mr: 2}} onClick={handleDeleteFileModalOkButtonClick}>Ok</Button>
              <Button variant="outlined" color="primary" onClick={handleDeleteFileModalCancelButtonClick}>Cancel</Button>
            </Box>
          </Box>
        </Modal>

        {/* Older-than-active snapshot upload confirmation modal */}
        <Modal
          open={showOlderSnapshotModal}
          aria-labelledby="modal-older-snapshot"
          aria-describedby="modal-older-snapshot">
          <Box sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 520,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.grey[300]}`,
            boxShadow: 24,
            p: 4,}}>
            <Typography id="modal-older-snapshot-title" variant="h6" component="h2">
              NOTE
            </Typography>
            <Typography id="modal-older-snapshot-description" sx={{ mt: 2, mb: 4 }}>
              The Snapshot Date / Acad Year selected is older than the existing active snapshot for the selected Client. This snapshot will not be reflected in the final data.
              <br />
              <br />
              Do you want to proceed?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button variant="contained" color="primary" sx={{mr: 2}} onClick={handleOlderSnapshotModalYesButtonClick}>Yes</Button>
              <Button variant="outlined" color="primary" onClick={handleOlderSnapshotModalCancelButtonClick}>Cancel</Button>
            </Box>
          </Box>
        </Modal>
      </Box>
    </Box>
  );
}

const askOlderSnapshotUploadConfirmation = (
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>,
  resolverRef: React.MutableRefObject<((proceed: boolean) => void) | null>
): Promise<boolean> => {
  setShowModal(true);
  return new Promise<boolean>((resolve) => {
    resolverRef.current = resolve;
  });
};

const isOlderThanActiveSnapshot = (
  academicYear: string,
  snapshotDate: string,
  activeSnapshot: { academic_year: string; snapshot_date: string }
): boolean => {
  const toInt = (v: string): number => parseInt((v || '').trim(), 10);

  // Explicit integer normalization for both selected form values and active-snapshot API values.
  const selectedYear = toInt(academicYear);
  const selectedDate = toInt(snapshotDate);
  const activeYear = toInt(activeSnapshot.academic_year);
  const activeDate = toInt(activeSnapshot.snapshot_date);

  console.log(
    `SnapshotAddUpdate.isOlderThanActiveSnapshot :: selectedYear=${selectedYear}, selectedDate=${selectedDate}, activeYear=${activeYear}, activeDate=${activeDate}`
  );

  const isSelectedYearOlder = Number.isFinite(selectedYear) && Number.isFinite(activeYear)
    ? selectedYear < activeYear
    : false;

  const isSelectedDateOlder = Number.isFinite(selectedDate) && Number.isFinite(activeDate)
    ? selectedDate < activeDate
    : false;

  return isSelectedYearOlder || isSelectedDateOlder;
};


function getDefaultFormValues(snapshot: AmaV2Snapshot | null, selectedAcademicYear: string): IFormInputs {
  
  if (snapshot) {
    return {
      client_id: snapshot.client_id,
      academic_year: snapshot.academic_year!,
      snapshot_date: snapshot.snapshot_date,
      description: snapshot.description || '',
      notes: snapshot.notes || '',
      is_active: snapshot.is_active,
      client_comparison: snapshot.client_comparison ?? false,
    };
  }
  
  return {
    client_id: '',
    academic_year: selectedAcademicYear,
    snapshot_date: '',
    description: '',
    notes: '',
    is_active: true,
    client_comparison: false,
  };
};

const mapClientsXtd = (clients: Array<AmaV2Client> | null) : Array<IClientXtd> => {
  if (!clients) return [];
  const activeClients = clients.filter(client => client.is_active === true);
  return activeClients.map(client => ({
    ...client,
    label: `${client.client_id}`
  }));
};

const saveSteps = [
  {
    label: 'Saving snapshot...',
    description: 'Snapshot resources are being created.'
  },
  {
    label: 'Uploading files...',
    description: 'Your file is being ingested to Bronze and ETL processing will begin shortly. Sign up for email notifications and you will be notified when processing is complete.'
  }
];

// Mapping helper implementing user-defined specification.
const mapSnapshotV2StatusToTileStatus = (
  snapshotStatus: SnapshotV2Status | undefined,
  currentStatus: FileStatus
): FileStatus => {
  if (!snapshotStatus) return currentStatus;
  switch (snapshotStatus) {
    case SnapshotV2Status.FAILED_WITH_ERRORS:
      return FileStatus.Error;
    case SnapshotV2Status.COMPLETED:
    case SnapshotV2Status.COMPLETED_WITH_WARNINGS:
      return FileStatus.Uploaded;
    case SnapshotV2Status.INGESTING_BRONZE:
    case SnapshotV2Status.BRONZE_COMPLETE:
    case SnapshotV2Status.PROCESSING_SILVER:
    case SnapshotV2Status.SILVER_COMPLETE:
    case SnapshotV2Status.PROCESSING_GOLD:
    case SnapshotV2Status.GOLD_COMPLETE:
      return FileStatus.Processing;
    case SnapshotV2Status.WAITING: // preserve whatever stage (Empty/Collected/Uploading)
    default:
      return currentStatus;
  }
}